import { useState, useEffect } from 'react';
import axios from 'axios';
import Pagination from '../../component/Admin/Pagination';
import { FaEdit, FaPlusCircle, FaTrashAlt, FaSearch, FaSort, FaSortUp, FaSortDown, FaUserCog } from 'react-icons/fa';
import Loading from '../../component/Loading';
import Modal from 'react-modal';
import swal from 'sweetalert';
import { adminTable } from '../../component/Admin/adminTable';
import SortBar from '../../component/Admin/SortBar';

const Admin = ({ data }) => {
    const [listAdmins, setListAdmins] = useState([]);
    const [meta, setMeta] = useState({});
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalData, setModalData] = useState(null);

    // Search and Sort states
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

    useEffect(() => {
        getListAdmins();
        // eslint-disable-next-line
    }, [page, limit, searchTerm, sortField, sortDirection]);

    const getListAdmins = () => {
        setLoading(true);
        const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
        const sortParam = sortField ? `&sortBy=${sortField}&sortOrder=${sortDirection.toUpperCase()}` : '';
        axios.get(`api/admin?page=${page}&limit=${limit}${searchParam}${sortParam}`, {
            withCredentials: true
        })
            .then((response) => {
                setMeta(response.data.meta);
                setListAdmins(response.data.items);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
                setLoading(false);
            });
    };

    const selectPage = (value) => {
        setPage(value);
    };

    const nextPage = () => {
        if (page < meta.totalPages) {
            setPage(page + 1);
        }
    };

    const prevPage = () => {
        if (page > 1) {
            setPage(page - 1);
        }
    };

    const limitPage = (event) => {
        setLimit(Number(event.target.value));
        setPage(1);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setModalData(null);
    };

    const handleClick = (id) => {
        const admin = listAdmins.find(item => item.id === id);
        if (admin) {
            setModalData(admin);
            setModalIsOpen(true);
        } else {
            setModalData(null);
            setModalIsOpen(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setLoading(true);
        const formData = new FormData(event.target);
        const data = Object.fromEntries(formData.entries());

        if (modalData && modalData.id) {
            // Update existing admin
            axios.put(`api/admin/${modalData.id}`, data, {
                withCredentials: true
            })
                .then(() => {
                    swal("Berhasil", "Data admin berhasil diupdate!", "success");
                    getListAdmins();
                    closeModal();
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error updating data:', error);
                    swal("Gagal", error.response?.data?.message || "Gagal mengupdate admin", "error");
                    setLoading(false);
                });
        } else {
            // Add new admin
            axios.post('api/admin', data, {
                withCredentials: true
            })
                .then(() => {
                    swal("Berhasil", "Admin berhasil ditambahkan!", "success");
                    getListAdmins();
                    closeModal();
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error adding data:', error);
                    swal("Gagal", error.response?.data?.message || "Gagal menambahkan admin", "error");
                    setLoading(false);
                });
        }
    };

    const handleDelete = (id) => {
        swal({
            title: "Apakah Anda yakin?",
            text: "Data admin akan dihapus secara permanen!",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        })
            .then((willDelete) => {
                if (willDelete) {
                    setLoading(true);
                    axios.delete(`api/admin/${id}`, {
                        withCredentials: true
                    })
                        .then(() => {
                            swal("Berhasil", "Admin berhasil dihapus!", "success");
                            getListAdmins();
                            setLoading(false);
                        })
                        .catch((error) => {
                            console.error('Error deleting data:', error);
                            swal("Gagal", error.response?.data?.message || "Gagal menghapus admin", "error");
                            setLoading(false);
                        });
                }
            });
    };

    const openAddModal = () => {
        setModalData({
            username: "",
            password: "",
            nama: "",
            level: "admin"
        });
        setModalIsOpen(true);
    };

    // Handle sort
    const handleSort = (field) => {
        if (field == null) {
            setSortField(null);
            setSortDirection('asc');
            setPage(1);
            return;
        }
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setPage(1);
    };

    // Get sort icon
    const getSortIcon = (field) => {
        if (sortField !== field) {
            return <FaSort className="ml-1 text-gray-400" />;
        }
        return sortDirection === 'asc'
            ? <FaSortUp className="ml-1 text-white" />
            : <FaSortDown className="ml-1 text-white" />;
    };

    return (
        <>
            {loading ? <Loading /> : null}

            <div className={adminTable.pageHeader}>
                <div>
                    <h1 className={adminTable.pageTitle}>
                        <FaUserCog className="text-[#1B3C33]" /> Data Admin
                    </h1>
                    <p className={adminTable.pageSubtitle}>Kelola akun administrator</p>
                </div>
                <button className={adminTable.btnPrimary} onClick={openAddModal} type="button">
                    <FaPlusCircle /> Tambah Admin
                </button>
            </div>

            <div className={adminTable.searchToolbar}>
                <div className={adminTable.searchWrap}>
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Cari username, nama, atau level..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1);
                        }}
                        className={adminTable.searchInput}
                    />
                </div>
                <SortBar
                    options={[
                        { value: 'nama', label: 'Nama' },
                        { value: 'username', label: 'Username' },
                        { value: 'level', label: 'Level' },
                    ]}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                />
            </div>

            <div>
                <div className={adminTable.list}>
                    {listAdmins.length > 0 ? (
                        listAdmins.map((item, index) => (
                            <div key={item.id || index} className={adminTable.listItem}>
                                <div className={adminTable.listTop}>
                                    <div className="min-w-0 flex-1">
                                        <div className={adminTable.listTitle}>{item.nama}</div>
                                        <div className={adminTable.listSub}>@{item.username} · {item.level}</div>
                                    </div>
                                    <div className={adminTable.listActions}>
                                        <button type="button" onClick={() => handleClick(item.id)} className={`${adminTable.actionBtn} text-zafu-green hover:bg-zafu-cream`} title="Edit">
                                            <FaEdit />
                                        </button>
                                        <button type="button" onClick={() => handleDelete(item.id)} className={`${adminTable.actionBtn} text-red-500 hover:bg-red-50`} title="Hapus">
                                            <FaTrashAlt />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className={adminTable.listEmpty}>
                            {searchTerm ? 'Data tidak ditemukan' : 'Data Admin Tidak Ditemukan'}
                        </div>
                    )}
                </div>

                <div className={`${adminTable.card} ${adminTable.desktopOnly}`}>
                    <div className={adminTable.scroll}>
                        <table className={adminTable.table}>
                            <thead className={adminTable.thead}>
                                <tr>
                                    <th className={adminTable.thCenter}>No.</th>
                                    <th className={adminTable.thSortable} onClick={() => handleSort('username')}>
                                        <div className="flex items-center">Username {getSortIcon('username')}</div>
                                    </th>
                                    <th className={adminTable.thSortable} onClick={() => handleSort('nama')}>
                                        <div className="flex items-center">Nama {getSortIcon('nama')}</div>
                                    </th>
                                    <th className={adminTable.thSortable} onClick={() => handleSort('level')}>
                                        <div className="flex items-center">Level {getSortIcon('level')}</div>
                                    </th>
                                    <th className={adminTable.thCenter}>Aksi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listAdmins.length > 0 ? (
                                    listAdmins.map((item, index) => (
                                        <tr key={item.id || index} className={adminTable.tr}>
                                            <td className={adminTable.tdCenter}>{(page - 1) * limit + index + 1}</td>
                                            <td className={adminTable.td}>{item.username}</td>
                                            <td className={`${adminTable.td} font-medium`}>{item.nama}</td>
                                            <td className={adminTable.td}>{item.level}</td>
                                            <td className={adminTable.tdCenter}>
                                                <div className={adminTable.actions}>
                                                    <button type="button" onClick={() => handleClick(item.id)} className={`${adminTable.actionBtn} text-zafu-green hover:bg-zafu-cream`} title="Edit">
                                                        <FaEdit />
                                                    </button>
                                                    <button type="button" onClick={() => handleDelete(item.id)} className={`${adminTable.actionBtn} text-red-500 hover:bg-red-50`} title="Hapus">
                                                        <FaTrashAlt />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} className={adminTable.empty}>
                                            {searchTerm ? 'Data tidak ditemukan' : 'Data Admin Tidak Ditemukan'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="mt-3">
                    <Pagination
                        currentPage={page}
                        totalPages={meta.totalPages}
                        limitPage={limitPage}
                        selectPage={selectPage}
                        nextPage={nextPage}
                        prevPage={prevPage}
                    />
                </div>
            </div>

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                contentLabel="Admin Modal"
                ariaHideApp={false}
                className="fixed w-screen inset-0 flex items-center justify-center h-screen p-4 bg-gray-500 bg-opacity-50"
                overlayClassName="fixed z-50 w-screen h-screen inset-0 bg-gray-800 bg-opacity-75"
            >
                {modalData && (
                    <div className='shadow-lg rounded-lg bg-white md:w-2/3 w-full max-h-[90vh] p-4 overflow-y-auto'>
                        <div className='text-center pt-2 text-xl font-semibold mb-4'>
                            {modalData.id ? 'EDIT ADMIN' : 'TAMBAH ADMIN'}
                        </div>
                        <form id='form-Admin' className="mt-6" onSubmit={handleSubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="username" className="block mt-2 text-xs font-semibold text-gray-600 uppercase">
                                        Username
                                    </label>
                                    <input
                                        id="username"
                                        type="text"
                                        name="username"
                                        placeholder="Username"
                                        className="block w-full p-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner rounded"
                                        required
                                        defaultValue={modalData.username}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="nama" className="block mt-2 text-xs font-semibold text-gray-600 uppercase">
                                        Nama
                                    </label>
                                    <input
                                        id="nama"
                                        type="text"
                                        name="nama"
                                        placeholder="Nama Admin"
                                        className="block w-full p-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner rounded"
                                        required
                                        defaultValue={modalData.nama}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="level" className="block mt-2 text-xs font-semibold text-gray-600 uppercase">
                                        Level
                                    </label>
                                    <select
                                        name='level'
                                        className="block w-full p-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner rounded"
                                        required
                                        defaultValue={modalData.level}
                                    >
                                        <option value="admin">Admin</option>
                                        <option value="superadmin">Super Admin</option>
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="password" className="block mt-2 text-xs font-semibold text-gray-600 uppercase">
                                        Password {modalData.id && '(Kosongkan jika tidak ingin mengubah)'}
                                    </label>
                                    <input
                                        id="password"
                                        type="password"
                                        name="password"
                                        placeholder="Password"
                                        className="block w-full p-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner rounded"
                                        required={!modalData.id}
                                        minLength={6}
                                    />
                                </div>
                            </div>

                            <div className="flex gap-4 mt-6">
                                <button
                                    type="submit"
                                    className="flex-1 py-3 font-medium tracking-widest text-white uppercase bg-[#1B3C33] shadow-lg focus:outline-none hover:bg-[#4a1f20] hover:shadow-none rounded"
                                >
                                    Simpan
                                </button>
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="flex-1 py-3 font-medium tracking-widest text-white uppercase bg-gray-500 shadow-lg focus:outline-none hover:bg-gray-600 hover:shadow-none rounded"
                                >
                                    Batal
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default Admin;

